import React from 'react';
import { Helmet } from 'react-helmet-async';
import PublicHeader from '../components/Layout/PublicHeader';
import StaticPageTopbar from '../components/Layout/StaticPageTopbar';
import romanPortrait from '../assets/team/roman.jpg';
import philippPortrait from '../assets/team/philipp.jpg';
import { SITE_ORIGIN } from '../config/siteOrigin';

// ── „Über uns" ────────────────────────────────────────────────────────────────────────────
// Stellt die zwei Personen hinter der Plattform vor (Roman: Redaktion/Community, Philipp:
// Technik/GSK-IT). Ersetzt den frueheren „Kontakt"-Link im Footer; auf Mobil (Tab-Bar ohne
// Footer) ueber Profil → Einstellungen erreichbar. Pixelgenaue Umsetzung der Design-Referenz
// „Rad Events – Web Prototyp" (Handoff „Über uns").

const FONT_DISPLAY = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const FONT_MONO = "'Space Mono',monospace";

const PAGE_TITLE = 'Über uns – Die Menschen hinter Radsport-Events.de | Radsport Events';
const PAGE_DESC =
  'Hinter radsport-events.de stehen zwei Menschen, die den Radsport leben: Roman Wisotzki (Gründer, Redaktion & Community) und Philipp Gottschalk (GSK-IT, technische Leitung). Gemeinsam entwickeln wir die Plattform weiter.';
const CANONICAL = `${SITE_ORIGIN}/ueber-uns`;

interface Person {
  name: string;
  role: string;
  bio: string;
  chips: string[];
  quote: string;
  img: string;
  imgStyle: React.CSSProperties;
  delay: string;
}

const PERSONS: Person[] = [
  {
    name: 'Philipp Gottschalk',
    role: 'Inhaber & Geschäftsführer GSK-IT',
    bio: 'Philipp hat radsport-events.de übernommen und leitet die Plattform, als Geschäftsführer von GSK-IT. Er verantwortet die technische Entwicklung, das neue System und die zukünftigen Funktionen — und bringt das Know-how mit, um die Plattform fit für die Zukunft zu machen.',
    chips: ['Technische Leitung', 'Infrastruktur', 'Neue Funktionen'],
    quote: '„ Moderne Technik für die Zukunft."',
    img: philippPortrait,
    imgStyle: { objectPosition: '50% 22%', transform: 'scale(1.3)', transformOrigin: '50% 26%' },
    delay: '0.05s',
  },
  {
    name: 'Roman Wisotzki',
    role: 'Gründer · Redaktion & Community',
    bio: 'Roman hat radsport-events.de gegründet und prägt die Plattform bis heute redaktionell. Er verantwortet die Inhalte, pflegt die Veranstaltungen und bringt die Weiterentwicklung aus Sicht der Radsport-Community voran.',
    chips: ['Redaktionelle Inhalte', 'Veranstaltungen', 'Community'],
    quote: '„ Leidenschaft für den Radsport."',
    img: romanPortrait,
    imgStyle: { objectPosition: '50% 26%', transform: 'scale(1.35)', transformOrigin: '50% 30%' },
    delay: '0.14s',
  },
];

const PHILOSOPHY = [
  'Vollständige Übersicht über Radsportveranstaltungen',
  'Aktuelle, zuverlässige Informationen',
  'Enger Austausch mit Veranstaltern und Community',
  'Kontinuierliche Weiterentwicklung der Plattform',
];

const UeberUns: React.FC = () => {
  return (
  <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
    <Helmet>
      <title>{PAGE_TITLE}</title>
      <meta name="description" content={PAGE_DESC} />
      <link rel="canonical" href={CANONICAL} />
      <meta property="og:title" content={PAGE_TITLE} />
      <meta property="og:description" content={PAGE_DESC} />
      <meta property="og:url" content={CANONICAL} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={PAGE_TITLE} />
      <meta name="twitter:description" content={PAGE_DESC} />
    </Helmet>

    <style>{`
      @keyframes wRise { from { opacity: 0; transform: translateY(26px); } to { opacity: 1; transform: translateY(0); } }
      .au-rise { animation: wRise 0.6s ease both; }

      @media (prefers-reduced-motion: reduce) {
        .au-rise { animation: none !important; opacity: 1 !important; transform: none !important; }
      }

      /* Responsive: Personen- und „Gemeinsam"-Grid einspaltig, Hero kompakter. */
      @media (max-width: 760px) {
        .au-hero { padding: 40px 20px 44px !important; }
        .au-hero-h1 { font-size: 34px !important; }
        .au-container { padding: 28px 20px 48px !important; }
        .au-persons { grid-template-columns: 1fr !important; }
        .au-together { padding: 32px 24px !important; }
        .au-together-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
      }
    `}</style>

    <div className="hide-mobile">
      <PublicHeader />
    </div>

    <StaticPageTopbar label="// ÜBER UNS" />

    {/* HERO-BAND */}
    <section
      className="au-hero"
      style={{
        background: 'linear-gradient(105deg,#3A1523 0%,#6E2440 52%,#A8355F 100%)',
        color: '#F5F3F3', padding: '48px 40px 54px',
      }}
    >
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <span style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.18em', color: '#F0C4D4' }}>{'// ÜBER UNS'}</span>
        <h1
          className="au-hero-h1"
          style={{
            fontFamily: FONT_DISPLAY, fontWeight: 900, fontSize: 46, lineHeight: 1.03,
            letterSpacing: '-0.025em', margin: '12px 0 0', maxWidth: 760, textWrap: 'pretty',
          }}
        >
          Aus Leidenschaft für den Radsport
        </h1>
        <p style={{
          fontFamily: FONT_BODY, fontSize: 18, lineHeight: 1.55, color: '#E9D6DE',
          margin: '16px 0 0', maxWidth: 640, textWrap: 'pretty',
        }}>
          Hinter radsport-events.de stehen zwei Menschen, die den Radsport leben — einer aus der
          Community heraus, einer aus der Technik. Gemeinsam entwickeln wir die Plattform weiter.
        </p>
      </div>
    </section>

    <div className="au-container" style={{ flex: 1, maxWidth: 1180, width: '100%', boxSizing: 'border-box', margin: '0 auto', padding: '44px 40px 60px' }}>

      {/* PERSONEN */}
      <div className="au-persons" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, alignItems: 'stretch' }}>
        {PERSONS.map((p) => (
          <article
            key={p.name}
            className="au-rise"
            style={{
              background: '#fff', border: '1px solid #E5E1E1', borderRadius: 22, padding: 28,
              display: 'flex', flexDirection: 'column', animationDelay: p.delay,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
              <div style={{
                flexShrink: 0, width: 84, height: 84, borderRadius: '50%', overflow: 'hidden',
                background: '#EDE9E9', border: '3px solid #F3E4EB',
              }}>
                <img
                  src={p.img}
                  alt={p.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', ...p.imgStyle }}
                />
              </div>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 24, letterSpacing: '-0.01em', color: '#2C2C2C', margin: 0 }}>
                  {p.name}
                </h2>
                <div style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.05em', color: '#922C57', margin: '6px 0 0' }}>
                  {p.role}
                </div>
              </div>
            </div>
            <p style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.6, color: '#4A4A4A', margin: '18px 0 0', textWrap: 'pretty' }}>
              {p.bio}
            </p>
            {/* marginBottom hier + marginTop:auto am Zitat = Zitat-Fusszeile sitzt in beiden
                Karten unten buendig, mit mindestens 20px Abstand (Design: margin-top 20px). */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '16px 0 20px' }}>
              {p.chips.map((chip) => (
                <span
                  key={chip}
                  style={{
                    fontFamily: FONT_BODY, fontWeight: 600, fontSize: 12.5, color: '#6E2440',
                    background: 'rgba(146,44,87,0.08)', border: '1px solid rgba(146,44,87,0.16)',
                    borderRadius: 999, padding: '6px 13px',
                  }}
                >
                  {chip}
                </span>
              ))}
            </div>
            <div style={{
              marginTop: 'auto', paddingTop: 18, borderTop: '1px solid #EDE9E9',
              fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.04em', color: '#9A9494',
            }}>
              {p.quote}
            </div>
          </article>
        ))}
      </div>

      {/* GEMEINSAM */}
      <section
        className="au-rise au-together"
        style={{
          marginTop: 28, borderRadius: 22, background: '#2C2C2C', color: '#F5F3F3',
          padding: '44px 48px', animationDelay: '0.22s',
        }}
      >
        <div className="au-together-grid" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 40, alignItems: 'center' }}>
          <div>
            <span style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.16em', color: 'rgba(245,243,243,0.55)' }}>
              {'// GEMEINSAM IN DIE ZUKUNFT'}
            </span>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 900, fontSize: 30, letterSpacing: '-0.02em', lineHeight: 1.1, margin: '14px 0 0' }}>
              Zwei Perspektiven, ein Ziel.
            </h2>
            <p style={{ fontFamily: FONT_BODY, fontSize: 16, lineHeight: 1.6, color: 'rgba(245,243,243,0.82)', margin: '14px 0 0', textWrap: 'pretty' }}>
              Seit Juli 2026 führen wir radsport-events.de zusammen weiter — Romans redaktionelles
              Gespür und die Nähe zur Community, Philipps technisches Fundament. Unser Ziel: das
              Bewährte erhalten und die Möglichkeiten einer modernen Plattform nutzen.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {PHILOSOPHY.map((point) => (
              <div key={point} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{
                  width: 9, height: 9, borderRadius: 2, transform: 'rotate(45deg)',
                  background: '#A8355F', flexShrink: 0, marginTop: 6,
                }} />
                <span style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.5, color: 'rgba(245,243,243,0.9)' }}>
                  {point}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
          flexWrap: 'wrap', marginTop: 34, paddingTop: 24, borderTop: '1px solid rgba(245,243,243,0.14)',
        }}>
          <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontStyle: 'italic', fontSize: 22, color: '#F5F3F3' }}>
            Roman &amp; Philipp
          </span>
          <span style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.08em', color: 'rgba(245,243,243,0.5)' }}>
            {'// von der Community, für die Community'}
          </span>
        </div>
      </section>
    </div>

  </div>
  );
};

export default UeberUns;
